import React from 'react';
import {
  useProductBranding,
  useUpdateProductBranding,
  type LogoStyle,
} from '../../../../../shared/hooks/useProductBranding';

export interface LogoStyleSelectorProps {
  productId: string | null;
}

const OPTIONS: Array<{
  value: LogoStyle;
  label: string;
  description: string;
}> = [
  {
    value: 'auto_contrast',
    label: 'Auto contrast',
    description: 'Pick the plate per context (recommended).',
  },
  {
    value: 'transparent',
    label: 'Transparent',
    description: 'No plate. Best for logos with adequate built-in contrast.',
  },
  {
    value: 'light_plate',
    label: 'Light plate',
    description:
      'White background with rounded corners — keeps dark logos readable on dark surfaces.',
  },
  {
    value: 'dark_plate',
    label: 'Dark plate',
    description: 'Dark background — for light/single-colour logos on white surfaces.',
  },
];

export const LogoStyleSelector: React.FC<LogoStyleSelectorProps> = ({ productId }) => {
  const branding = useProductBranding(productId);
  const update = useUpdateProductBranding();
  const current = branding.data?.logoStyle ?? 'auto_contrast';

  const handleChange = (next: LogoStyle) => {
    if (!productId) return;
    update.mutate({ productId, logoStyle: next });
  };

  return (
    <div className="rb-logo-style" role="radiogroup" aria-label="Logo style">
      {OPTIONS.map((opt) => {
        const active = current === opt.value;
        return (
          <label
            key={opt.value}
            className={`rb-logo-style__option${active ? ' rb-logo-style__option--active' : ''}`}
          >
            <input
              type="radio"
              name={`logo-style-${productId ?? 'none'}`}
              value={opt.value}
              checked={active}
              onChange={() => handleChange(opt.value)}
              disabled={!productId || update.isPending}
            />
            <span className="rb-logo-style__option-body">
              <span className="rb-logo-style__option-label">{opt.label}</span>
              <span className="rb-logo-style__option-desc">{opt.description}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
};
